import { VideoPlayer } from "@seeen/player";
import { useApiVideo } from "../shared/useApiVideo";
import type { CtaOverlay, VideoItem } from "../shared/types";
import type { Moment } from "@seeen/player";

/** Pre-fetched mode: supply a `VideoItem` directly. */
type WithVideo = { video: VideoItem; apiKey?: never; videoId?: never };
/** API-driven mode: supply credentials and the embed fetches the video. */
type WithApi = { apiKey: string; videoId: string; video?: never };

/**
 * Props for `SingleEmbed`. Accepts either a pre-fetched `video` object
 * or an `apiKey` + `videoId` pair for API-driven loading.
 */
export type SingleEmbedProps = (WithVideo | WithApi) & {
  /** CTA image overlays displayed at fixed positions over the video. */
  overlays?: CtaOverlay[];
  /** Start playing automatically on load. Most browsers require `muted`. */
  autoplay?: boolean;
  /** Mute the video on load. Required for autoplay in most browsers. */
  muted?: boolean;
  /** Pre-selects a moment (chapter), seeking to it on first play. */
  activeMoment?: Moment;
  /** Google Analytics 4 measurement ID (e.g. `"G-XXXXXXXXXX"`). */
  gaMeasurementId?: string;
  /** Start playback at this position in seconds. Falls back to the `?t=` query parameter. */
  startTime?: number;
  /** Pre-selects a moment by ID, resolved against the video's moments. Falls back to the `_seeen_moment` URL parameter. Ignored when `activeMoment` is set. */
  activeMomentId?: string;
  /** Show a "Watch Again / Resume Video" splash when the active moment ends. */
  momentEndSplash?: boolean;
  /** Position of the moments navigation bar. Defaults to `"above"`. Set to `"none"` to hide it. */
  momentsNavPosition?: "above" | "below" | "none";
  /** What clicking a moment in the moments nav does. `"moment"` (default) treats it like `activeMomentId`, stopping at the moment's end and showing the end splash; `"seek"` only jumps to it. */
  momentNavBehavior?: "seek" | "moment";
  /** Show the social share popover. Defaults to `undefined` (show if the API returns icons). Set to `false` to force-hide. */
  share?: boolean;
};

function resolveActiveMomentId(prop: string | undefined): string | undefined {
  if (prop != null) return prop;
  return (
    new URLSearchParams(
      typeof window !== "undefined" ? window.location.search : "",
    ).get("_seeen_moment") ?? undefined
  );
}

function resolveStartTime(prop: number | undefined): number | undefined {
  if (prop != null) return prop;
  const t = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  ).get("t");
  if (t == null) return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function SingleEmbed({
  overlays = [],
  autoplay = false,
  muted = false,
  activeMoment,
  activeMomentId: activeMomentIdProp,
  gaMeasurementId,
  startTime: startTimeProp,
  momentEndSplash,
  momentsNavPosition,
  momentNavBehavior,
  share,
  ...props
}: SingleEmbedProps) {
  const startTime = resolveStartTime(startTimeProp);
  const activeMomentId = resolveActiveMomentId(activeMomentIdProp);
  const apiKey =
    "apiKey" in props && props.apiKey != null ? props.apiKey : null;
  const videoId =
    "videoId" in props && props.videoId != null ? props.videoId : null;
  const apiState = useApiVideo(apiKey, videoId);

  const video =
    "video" in props
      ? props.video
      : apiState.status === "success"
        ? apiState.video
        : null;

  if (apiState.status === "loading") {
    return <div className="aspect-video w-full bg-black animate-pulse" />;
  }

  if (apiState.status === "error") {
    return (
      <div className="aspect-video w-full bg-black flex items-center justify-center">
        <p className="text-white/50 text-sm">Failed to load video.</p>
      </div>
    );
  }

  if (!video) return null;

  const ctaTimestamps =
    apiState.status === "success" ? apiState.video.ctaTimestamps : undefined;
  const endCard =
    apiState.status === "success" ? apiState.video.endCard : undefined;
  const shareSocialMediaIcons =
    share === false
      ? undefined
      : apiState.status === "success"
        ? apiState.video.shareSocialMediaIcons
        : video.shareSocialMediaIcons;
  const captions =
    apiState.status === "success" ? apiState.video.captions : undefined;
  const moments =
    apiState.status === "success" ? apiState.video.moments : undefined;
  const structuredData =
    apiState.status === "success" ? apiState.video.structuredData : undefined;

  return (
    <VideoPlayer
      src={video.src}
      poster={video.poster}
      title={video.title}
      thumbnailStoryboardUrl={video.thumbnailStoryboardUrl}
      captions={captions}
      overlays={overlays}
      sponsor={video.sponsor}
      ctaTimestamps={ctaTimestamps}
      endCard={endCard}
      autoplay={autoplay}
      muted={muted}
      shareSocialMediaIcons={shareSocialMediaIcons}
      moments={moments}
      activeMoment={activeMoment}
      activeMomentId={activeMomentId}
      structuredData={structuredData}
      gaMeasurementId={gaMeasurementId}
      startTime={startTime}
      momentEndSplash={momentEndSplash}
      momentsNavPosition={momentsNavPosition}
      momentNavBehavior={momentNavBehavior}
    />
  );
}
